import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getOrganizations, getOrganization } from '../lib/api';
import { useAuth } from './AuthContext';

const AdminModeContext = createContext(null);

// Modules available to prospects (restricted access)
const PROSPECT_MODULES = [
  '/org-iq',
  '/monitoring',
  '/assessments',
  '/settings'
];

// Modules that require active customer status
const CUSTOMER_ONLY_MODULES = [
  '/incidents',
  '/staff',
  '/policies',
  '/lms',
  '/training',
  '/alerts',
  '/events',
  '/claims'
];

// Lifecycle statuses that indicate prospect status (not yet customer)
const PROSPECT_STATUSES = [
  'lead',
  'prospect', 
  'intake_in_progress',
  'assessment_completed',
  'documents_uploaded',
  'under_review',
  'proposal_sent',
  'closed_won', // Still prospect until activated
  'closed_lost'
];

// Helper to safely get from sessionStorage
const getPersistedOrg = () => {
  try {
    const saved = sessionStorage.getItem('selectedOrg');
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

// Helper to safely get persisted mode
const getPersistedMode = () => {
  try {
    return sessionStorage.getItem('operatingMode') || 'platform';
  } catch {
    return 'platform';
  }
};

export const AdminModeProvider = ({ children }) => {
  const { isSuperAdmin, isAuthenticated, user } = useAuth();
  
  // Operating mode: 'platform' = god view / 'organization' = org-specific view
  // Initialize from sessionStorage for persistence across navigation
  const [operatingMode, setOperatingMode] = useState(getPersistedMode);
  
  // Selected organization when in organization mode
  // Initialize from sessionStorage for persistence across navigation
  const [selectedOrg, setSelectedOrg] = useState(getPersistedOrg);
  
  // All organizations for selection
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  
  // Persist operatingMode to sessionStorage whenever it changes
  useEffect(() => {
    try {
      sessionStorage.setItem('operatingMode', operatingMode);
    } catch {
      // Ignore storage errors
    }
  }, [operatingMode]);
  
  // Persist selectedOrg to sessionStorage whenever it changes
  useEffect(() => {
    try {
      if (selectedOrg) {
        sessionStorage.setItem('selectedOrg', JSON.stringify(selectedOrg));
      } else {
        sessionStorage.removeItem('selectedOrg');
      }
    } catch {
      // Ignore storage errors
    }
  }, [selectedOrg]);

  // Fetch organizations on mount (only when authenticated and super admin)
  const fetchOrganizations = useCallback(async () => {
    if (!isAuthenticated || !isSuperAdmin()) return;
    
    setLoadingOrgs(true);
    try {
      const response = await getOrganizations();
      const orgs = response.data || [];
      
      // Sort organizations hierarchically: National > Regional > State > Local > Franchise > Franchisee
      const levelOrder = {
        'national': 0,
        'regional': 1,
        'state': 2,
        'local': 3,
        'franchise': 4,
        'franchisee': 5
      };
      
      const sortedOrgs = orgs.sort((a, b) => {
        const levelA = levelOrder[a.level] ?? 99;
        const levelB = levelOrder[b.level] ?? 99;
        if (levelA !== levelB) return levelA - levelB;
        return a.name.localeCompare(b.name);
      });
      
      setOrganizations(sortedOrgs);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    } finally {
      setLoadingOrgs(false);
    }
  }, [isSuperAdmin, isAuthenticated]);

  // Auto-set organization for org admins based on their user profile
  const fetchUserOrganization = useCallback(async () => {
    if (!isAuthenticated || isSuperAdmin() || !user?.organization_id) return;
    
    setLoadingOrgs(true);
    try {
      const response = await getOrganization(user.organization_id);
      if (response.data) {
        setSelectedOrg(response.data);
        setOperatingMode('organization');
      }
    } catch (error) {
      console.error('Failed to fetch user organization:', error);
    } finally {
      setLoadingOrgs(false);
    }
  }, [isAuthenticated, isSuperAdmin, user?.organization_id]);

  useEffect(() => {
    if (isAuthenticated) {
      if (isSuperAdmin()) {
        fetchOrganizations();
      } else {
        fetchUserOrganization();
      }
    }
  }, [isAuthenticated, isSuperAdmin, fetchOrganizations, fetchUserOrganization]);

  // Switch to platform mode
  const switchToPlatformMode = useCallback(() => {
    setOperatingMode('platform');
    setSelectedOrg(null);
  }, []);

  // Switch to organization mode
  const switchToOrganizationMode = useCallback((org = null) => {
    setOperatingMode('organization');
    if (org) {
      setSelectedOrg(org);
    }
  }, []);

  // Select an organization (also switches to org mode)
  const selectOrganization = useCallback((orgId) => {
    const org = organizations.find(o => o.id === orgId);
    if (org) {
      setSelectedOrg(org);
      setOperatingMode('organization');
    }
  }, [organizations]);

  // Clear organization selection (stays in org mode but no org selected)
  const clearOrganization = useCallback(() => {
    setSelectedOrg(null);
  }, []);

  // Check if org is in prospect status
  const isProspectOrg = useCallback(() => {
    if (!selectedOrg) return false;
    return PROSPECT_STATUSES.includes(selectedOrg.lifecycle_status);
  }, [selectedOrg]);

  // Check if a module is accessible based on org lifecycle status
  const canAccessModule = useCallback((path) => {
    // Super admins can always access everything
    if (isSuperAdmin()) return true;
    
    // If no org selected, allow access (will show empty state)
    if (!selectedOrg) return true;
    
    // If org is a prospect, check if module is in prospect modules
    if (isProspectOrg()) {
      // Prospects can only access limited modules
      const isProspectModule = PROSPECT_MODULES.some(m => path.startsWith(m));
      return isProspectModule;
    }
    
    // Active customers can access everything
    return true;
  }, [selectedOrg, isProspectOrg, isSuperAdmin]);

  // Get list of restricted modules for current org
  const getRestrictedModules = useCallback(() => {
    if (!isProspectOrg()) return [];
    return CUSTOMER_ONLY_MODULES;
  }, [isProspectOrg]);

  const value = {
    operatingMode,
    selectedOrg,
    organizations,
    loadingOrgs,
    isPlatformMode: operatingMode === 'platform',
    isOrganizationMode: operatingMode === 'organization',
    hasOrgContext: !!selectedOrg,
    switchToPlatformMode,
    switchToOrganizationMode,
    selectOrganization,
    clearOrganization,
    refreshOrganizations: fetchOrganizations,
    // Module gating
    isProspectOrg,
    canAccessModule,
    getRestrictedModules,
    PROSPECT_MODULES,
    CUSTOMER_ONLY_MODULES,
  };

  return (
    <AdminModeContext.Provider value={value}>
      {children}
    </AdminModeContext.Provider>
  );
};

export const useAdminMode = () => {
  const context = useContext(AdminModeContext);
  if (!context) {
    throw new Error('useAdminMode must be used within an AdminModeProvider');
  }
  return context;
};
